import { useState } from 'react';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  Home,
  User,
  DollarSign,
  ChevronDown,
  Copy,
  Check,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useEncompassLoan } from '../../hooks/useLosProxy';

interface Props {
  loanId: string;
}

function LoanField({ label, value }: { label: string; value: string | number | undefined | null }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-xs text-gray-800 dark:text-gray-200 mt-0.5">{String(value)}</p>
    </div>
  );
}

export default function EncompassLoanViewer({ loanId }: Props) {
  const { data: loan, isLoading, error, refetch, isFetching } = useEncompassLoan(loanId);
  const [showRawJson, setShowRawJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(loan, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!loanId) {
    return (
      <div className="text-center py-4">
        <Home className="h-6 w-6 text-gray-300 dark:text-gray-600 mx-auto mb-1" />
        <p className="text-sm text-gray-500">No loan ID available for this order</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading loan data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <AlertTriangle className="h-6 w-6 text-amber-400 mx-auto mb-1" />
        <p className="text-sm text-gray-500">Failed to load Encompass loan</p>
        <p className="text-xs text-gray-400 mt-1">{(error as Error).message}</p>
        <button onClick={() => refetch()} className="btn-secondary text-xs mt-3">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-indigo-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Encompass Loan Data</h4>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} className="btn-icon p-1.5" title="Refresh">
          <RefreshCw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* Borrower Info */}
      {(loan?.borrowerFirstName || loan?.borrowerLastName) && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <User className="h-3 w-3 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Borrower</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pl-4">
            <LoanField label="First Name" value={loan.borrowerFirstName} />
            <LoanField label="Last Name" value={loan.borrowerLastName} />
          </div>
        </div>
      )}

      {/* Property Info */}
      {(loan?.propertyAddress || loan?.propertyCity) && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Home className="h-3 w-3 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Property</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pl-4">
            <LoanField label="Address" value={loan.propertyAddress} />
            <LoanField label="City" value={loan.propertyCity} />
            <LoanField label="State" value={loan.propertyState} />
            <LoanField label="ZIP" value={loan.propertyZip} />
          </div>
        </div>
      )}

      {/* Loan Info */}
      {(loan?.loanAmount || loan?.loanPurpose || loan?.loanType) && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <DollarSign className="h-3 w-3 text-gray-400" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Loan Details</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pl-4">
            <LoanField label="Amount" value={loan.loanAmount ? `$${loan.loanAmount.toLocaleString()}` : undefined} />
            <LoanField label="Purpose" value={loan.loanPurpose} />
            <LoanField label="Type" value={loan.loanType} />
            <LoanField label="Interest Rate" value={loan.interestRate ? `${loan.interestRate}%` : undefined} />
          </div>
        </div>
      )}

      {/* Loan ID */}
      <div className="pt-2 border-t border-gray-200 dark:border-gray-700/50">
        <LoanField label="Loan ID" value={loanId} />
      </div>

      {/* Raw JSON toggle */}
      <div>
        <button
          onClick={() => setShowRawJson(!showRawJson)}
          className="text-[11px] text-blue-500 hover:text-blue-400 flex items-center gap-1"
        >
          <ChevronDown className={clsx('h-3 w-3 transition-transform', showRawJson && 'rotate-180')} />
          {showRawJson ? 'Hide' : 'Show'} raw loan data
        </button>
        {showRawJson && (
          <div className="mt-2 relative">
            <button onClick={handleCopy} className="absolute top-2 right-2 btn-icon p-1" title="Copy JSON">
              {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
            </button>
            <pre className="text-[11px] text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50 rounded-lg p-3 overflow-auto max-h-72 border border-gray-200 dark:border-gray-700/50 font-mono">
              {JSON.stringify(loan, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
